import type { DeferredDownPaymentEntryV2, DeferredDownPaymentV2 } from "@credit-acceptance/step6-bff-client";
import { useTranslation } from "react-i18next";

import { formatIsoDate } from "../../../../lib/format";
import { formatCurrency, sumAmounts } from "../../../../lib/money";
import type { AuditTableColumn } from "../../../audit/AuditTable";
import SummaryTableModal from "../../components/SummaryTableModal";
import { EMPTY_VALUE, formatOptional } from "./financialFormat";

interface DeferredDownPaymentModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deferredDownPayment: DeferredDownPaymentV2;
}

// Read-only installment breakdown of the deferred down payment, opened from the "Cash Down" link
function DeferredDownPaymentModal({ open, onOpenChange, deferredDownPayment }: DeferredDownPaymentModalProperties) {
  const { t } = useTranslation();
  const entries = deferredDownPayment.entries ?? [];

  const columns: Array<AuditTableColumn<DeferredDownPaymentEntryV2>> = [
    {
      key: "date",
      header: t("financial.modalColumns.date"),
      render: (entry) => formatIsoDate(entry.date) || EMPTY_VALUE,
    },
    {
      key: "amount",
      header: t("financial.modalColumns.amount"),
      render: (entry) => formatOptional(entry.amount, formatCurrency),
    },
  ];

  return (
    <SummaryTableModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("financial.deferredDownPaymentModal.title")}
      description={t("financial.deferredDownPaymentModal.description")}
      caption={t("financial.deferredDownPaymentModal.caption")}
      summaryLines={[
        {
          label: t("financial.deferredDownPaymentModal.total"),
          value: formatCurrency(sumAmounts(entries.map((entry) => ({ amount: entry.amount ?? 0 })))),
        },
        {
          label: t("financial.deferredDownPaymentModal.maxDeferredDownDate"),
          value: formatIsoDate(deferredDownPayment.maxDeferredDownDate) || EMPTY_VALUE,
        },
      ]}
      columns={columns}
      rows={entries}
    />
  );
}

export default DeferredDownPaymentModal;
